import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { getIn } from 'formik';
import { useTranslation } from '../../../../src/i18n';
import { PERFORMANCE_TYPES_ID, TP, VIDEO_ATTRIBUTE_SLUGS } from '../../../../constants/consts';
import FbCircularProgress from '../../../../components/FbCircularProgress';
import EditMediaLayout from '../../../../components/EditComponents/EditMediaLayout';
import ConfirmDeleteModal from '../../../../components/EditComponents/ConfirmDeleteModal';
import VideoOnDemandEditCard from '../VideoOnDemandEditCard';
import {
  selectLivesEvents,
  selectLoadingFieldName,
  selectProduction,
  selectProductionVideoOnDemandData,
} from '../../selectors';
import { getTimezonesOptions } from '../../../../utils/common';
import DigitalEventModal from '../DigitalEventModal';
import { VODCardFields, commonDigitalEventFields } from '../DigitalEventModal/consts';
import { getMediaBodyForModifying } from '../../utils';
import { updateOne } from '../../../App/actions';
import { MEDIA } from '../../../../constants/endpointConstants';
import { createDigitalEventAction, deleteDigitalEventAction, updateVideoOnDemandAction } from '../../actions';
import { DIGITAL_EVENT_TYPES } from '../../constants';

const VideoOnDemand = ({ className }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const videosOnDemand = useSelector(selectProductionVideoOnDemandData());
  const liveEvents = useSelector(selectLivesEvents());
  const production = useSelector(selectProduction());
  const updatingCardId = useSelector(selectLoadingFieldName());
  const [VODToEdit, setVODToEdit] = React.useState(null);
  const [VODToDelete, setVODToDelete] = React.useState(null);

  const timezonesOptions = React.useCallback(() => getTimezonesOptions(), []);

  const openModal = record => {
    setVODToEdit(record);
  };

  const closeModal = () => {
    setVODToEdit(null);
  };

  const saveRecord = recordFormValues => {
    const updatedMedia = getMediaBodyForModifying({
      specificAttributes: [
        // VODCardFields.endTime,
        VODCardFields.dateFrom,
        VODCardFields.dateTo,
        // commonDigitalEventFields.timezone,
      ],
      recordToEdit: VODToEdit,
      recordFormValues,
      productionId: production?.id,
      recordTypeSlug: VIDEO_ATTRIBUTE_SLUGS.VIDEO_ON_DEMAND,
      formFields: VODCardFields,
    });
    const image = getIn(recordFormValues, commonDigitalEventFields.thumbnail.name);
    if (recordFormValues?.id) {
      closeModal();
      dispatch(
        updateOne({
          endpoint: MEDIA,
          sagaRoutine: updateVideoOnDemandAction,
          id: recordFormValues?.id,
          fieldName: recordFormValues?.id,
          params: updatedMedia,
          ...(image ? { files: { [image?.filename]: image?.file } } : {}),
        }),
      );
    } else {
      dispatch(
        createDigitalEventAction({
          params: updatedMedia,
          ...(image ? { files: { [image?.filename]: image?.file } } : {}),
          fieldName: 'new',
          typeId: PERFORMANCE_TYPES_ID.VIDEO_ON_DEMAND,
          typeName: t(`${TP}.FN_VIDEO_ON_DEMAND`),
          callback: closeModal,
          production,
          listLength: videosOnDemand?.data?.length,
        }),
      );
    }
  };

  const deleteTagFromRecord = () => {
    setVODToDelete(null);
    dispatch(
      deleteDigitalEventAction({
        id: VODToDelete?.id,
        fieldName: VODToDelete?.id,
        typeId: PERFORMANCE_TYPES_ID.VIDEO_ON_DEMAND,
        typeName: t(`${TP}.FN_VIDEO_ON_DEMAND`),
        production,
        listLength: videosOnDemand?.data?.length,
      }),
    );
  };

  return (
    <>
      <EditMediaLayout
        title={t(`${TP}.FN_VIDEO_ON_DEMAND`)}
        subtitle={t(`${TP}.FN_UPDATE_PRODUCTION_VIDEO_ON_DEMAND_SUBTITLE`)}
        isEmpty={!videosOnDemand?.data?.length}
        buttonTitle={`${TP}.FN_ADD_VIDEO_ON_DEMAND`}
        addButtonClick={() => openModal({})}
        emptyMessage={`${TP}.FN_NO_VIDEO_ON_DEMAND`}
        className={className}
      >
        {videosOnDemand?.loading ? (
          <FbCircularProgress />
        ) : (
          videosOnDemand?.data?.map(record => (
            <VideoOnDemandEditCard
              key={record?.id}
              record={record}
              onDelete={() => setVODToDelete(record)}
              onEdit={() => openModal(record)}
              loading={updatingCardId === record?.id}
            />
          ))
        )}
      </EditMediaLayout>
      {VODToEdit && (
        <DigitalEventModal
          recordInfo={VODToEdit}
          timezonesOptions={timezonesOptions()}
          onClose={closeModal}
          isLoading={updatingCardId === 'new'}
          onSave={saveRecord}
          liveDates={liveEvents}
          type={DIGITAL_EVENT_TYPES.VOD}
          production={production}
        />
      )}
      {VODToDelete && (
        <ConfirmDeleteModal
          closeModal={() => setVODToDelete(null)}
          title={t(`${TP}.FN_DELETE_VIDEO_ON_DEMAND_CONFIRMATION`)}
          onDelete={deleteTagFromRecord}
        />
      )}
    </>
  );
};

VideoOnDemand.propTypes = {
  className: PropTypes.string,
};

VideoOnDemand.defaultProps = {
  className: '',
};

export default VideoOnDemand;
